if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.variantMediaMap = [];
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
      }

      connectedCallback() {
        const variantMapScript = this.querySelector('[data-variant-media-map]');
        if (variantMapScript) {
          try {
            this.variantMediaMap = JSON.parse(variantMapScript.textContent);
          } catch (error) {
            console.warn('Could not parse variant media map', error);
          }
        }

        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('click', this.onViewerClick.bind(this));

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch.querySelector('button').addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
              if (mediaToSwitch.dataset.variantId) {
                this.selectVariant(mediaToSwitch.dataset.variantId);
              }
            });
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onViewerClick(event) {
        const mediaItem = event.target.closest('.product__media-item[data-variant-id]');
        if (!mediaItem?.dataset.variantId) return;
        this.selectVariant(mediaItem.dataset.variantId);
      }

      selectVariant(variantId) {
        const variant = this.variantMediaMap.find((entry) => entry.id === Number(variantId));
        if (!variant) return;

        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) return;

        const currentVariantInput = productInfo.querySelector('input[name="id"]');
        if (currentVariantInput?.value === String(variant.id)) return;

        const optionContainers = variantSelects.querySelectorAll(
          'fieldset.product-form__input, .product-form__input--dropdown'
        );
        const inputsToSync = [];

        optionContainers.forEach((container, index) => {
          const optionValue = variant.options[index];
          if (!optionValue) return;

          const radio = Array.from(container.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === optionValue
          );
          if (radio) {
            radio.checked = true;
            inputsToSync.push(radio);
            return;
          }

          const select = container.querySelector('select');
          if (select) {
            const option = Array.from(select.options).find((opt) => opt.value === optionValue);
            if (option) {
              select.value = option.value;
              inputsToSync.push(select);
            }
          }
        });

        if (!inputsToSync.length) return;

        inputsToSync.forEach((input) => {
          variantSelects.updateSelectionMetadata({ target: input });
        });

        inputsToSync[inputsToSync.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
